import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { CALENDAR_PATH } from '@app/constants/navigation';
import { getAccessToken } from '@app/utils';

import { useAuthorizationAccessCode } from '@pages/Authorization/hooks/useAuthorizationAccessCode';
import { useAuthorizationEmail } from '@pages/Authorization/hooks/useAuthorizationEmail';
import { useAuthorizationPassword } from '@pages/Authorization/hooks/useAuthorizationPassword';
import { useAuthorizationSecret } from '@pages/Authorization/hooks/useAuthorizationSecret';

import type { AuthorizationTabKey } from '@pages/Authorization/Authorization.types';
import { clearAuthorizationAccessToken } from '@pages/Authorization/utils/authorizationSession';

export const useAuthorization = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailFromParam = String(searchParams.get('email') || '');
  const accessCodeFromParam = String(searchParams.get('accessCode') || '');
  const hasAccessCodeParams = Boolean(emailFromParam && accessCodeFromParam);

  const accessToken = useMemo(() => getAccessToken(), []);

  const [email, setEmail] = useState(emailFromParam);
  const [isAccessCode, setAccessCode] = useState(hasAccessCodeParams);
  const [activeTabKey, setActiveTabKey] =
    useState<AuthorizationTabKey>('email');

  const { authorizeBySecretHandler, isSecret } = useAuthorizationSecret();

  useEffect(() => {
    if (!hasAccessCodeParams) {
      return;
    }

    setAccessCode(true);
    setEmail(emailFromParam);
    setActiveTabKey('email');
  }, [accessCodeFromParam, emailFromParam, hasAccessCodeParams]);

  const isAccessCodeComponent = useMemo(
    () => isAccessCode || hasAccessCodeParams,
    [hasAccessCodeParams, isAccessCode]
  );

  const emailModel = useAuthorizationEmail({
    email,
    isEnabled: activeTabKey === 'email' && !isAccessCodeComponent && !isSecret,
    setAccessCode,
    setEmail,
  });

  const passwordModel = useAuthorizationPassword({
    email,
    isEnabled: activeTabKey === 'password' && !isSecret,
    setEmail,
  });

  const accessCodeModel = useAuthorizationAccessCode({
    accessCodeValue: accessCodeFromParam,
    email,
    isEnabled: activeTabKey === 'email' && isAccessCodeComponent && !isSecret,
  });

  const hasUserAuthorized = useMemo(() => accessToken !== null, [accessToken]);

  const changeTabHandler = useCallback((tabKey: string) => {
    setActiveTabKey(tabKey as AuthorizationTabKey);
  }, []);

  const redirectAuthorizedHandler = useCallback(() => {
    navigate(CALENDAR_PATH);
  }, [navigate]);

  const clearAuthorizationAccessTokenHandler = useCallback(() => {
    clearAuthorizationAccessToken();
  }, []);

  return {
    accessCodeModel,
    activeTabKey,
    authorizeBySecretHandler,
    changeTabHandler,
    clearAuthorizationAccessTokenHandler,
    email,
    emailModel,
    hasUserAuthorized,
    isAccessCodeComponent,
    isSecret,
    passwordModel,
    redirectAuthorizedHandler,
  };
};
